import { useEffect, useState, type ReactNode } from 'react';
import { Sheet } from './Sheet';
import { Button } from './Button';
import { Notice } from './Badge';

/**
 * The shell every "fix this" form uses.
 *
 * Nothing in the spine could be corrected until recently, so these forms all arrived at
 * once — which is exactly when five slightly different Save buttons get written. One shell
 * keeps the behaviour identical: Save is disabled until something actually changed, the
 * server's complaint appears above the fields rather than in a toast that vanishes, and a
 * destructive action (archive, retire) is separated from the ordinary one by the footer.
 *
 * Closing over unsaved edits ASKS FIRST (104): Esc, the overlay, ✕ and Cancel all route
 * through the same gate, and the confirm is inline in the DangerAction idiom — this
 * file's own docstring bans a generic window.confirm. While the confirm is showing,
 * further close requests do nothing: a stray second Esc must not be the thing that
 * discards someone's typing.
 */
export function EditSheet({
  open,
  onClose,
  title,
  children,
  onSave,
  saving,
  saveDisabled,
  error,
  dirty,
  destructive,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  onSave: () => void;
  saving?: boolean;
  /** A retained form can remain dirty while a required reference refresh is unavailable. */
  saveDisabled?: boolean;
  error?: { message: string } | null;
  /** Nothing typed yet means nothing to save — a button that can only no-op shouldn't invite a click. */
  dirty: boolean;
  destructive?: ReactNode;
  /** The Sheet's 560px, for a form that earns it (the email wording sheet's text and preview). */
  wide?: boolean;
}) {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  // A consumer that keeps this mounted and toggles `open` must not reopen onto a stale
  // confirm from the last session with the form.
  useEffect(() => {
    if (!open) setConfirmingDiscard(false);
  }, [open]);

  const requestClose = () => {
    // A close request during the exit animation must not re-arm the confirm — the
    // DismissableLayer is still mounted for ~300ms after `open` flips, and the
    // !open reset effect has already run (review m2).
    if (!open) return;
    if (confirmingDiscard) return;
    // Mid-save OR dirty: ask, with copy that tells the truth for each. A hard
    // `if (saving) return` held the sheet HOSTAGE (Codex 104-1): mutations here are
    // offlineFirst with no timeout, so an offline save keeps isPending forever and
    // Esc, overlay, ✕ and Cancel would all silently no-op with no way out.
    if (saving || dirty) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={requestClose}
      title={title}
      wide={wide}
      footer={
        confirmingDiscard ? (
          <AskFirst
            question={saving ? 'A save is still in flight' : 'Discard unsaved changes?'}
            detail={
              saving
                ? "Closing won't cancel it — you just won't see here whether it landed."
                : 'Nothing has been saved — whatever you typed here will be lost.'
            }
            confirmLabel={saving ? 'Close anyway' : 'Discard changes'}
            onConfirm={() => {
              setConfirmingDiscard(false);
              onClose();
            }}
            keepLabel={saving ? 'Wait for it' : 'Keep editing'}
            onKeep={() => setConfirmingDiscard(false)}
          />
        ) : (
          <div className="flex w-full flex-wrap items-center gap-2">
            <Button
              className="flex-1"
              disabled={!dirty || saving || saveDisabled}
              onClick={onSave}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-3">
        {error ? <Notice tone="danger">{error.message}</Notice> : null}
        {children}
        {destructive ? (
          <div className="mt-6 border-t border-border pt-4">{destructive}</div>
        ) : null}
      </div>
    </Sheet>
  );
}

/**
 * Send only what the user actually touched.
 *
 * Not just tidiness: in a two-person office both people have the customer open, and a form
 * that PUTs every field would silently overwrite whatever the other one just fixed. Sending
 * three keys instead of nine shrinks that window to the fields genuinely in dispute.
 *
 * A field emptied to `''` IS a change — that's how you delete a wrong phone number — so it
 * is compared against the original rather than dropped for being falsy.
 */
export function changedFields<
  T extends Record<string, string | boolean | null | undefined>,
>(original: T, draft: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(draft)) {
    const a = original[key] ?? '';
    const b = draft[key] ?? '';
    if (a !== b) out[key] = draft[key];
  }
  return out as Partial<T>;
}

/** Read a nullable column into a form field. `null` and `undefined` are both an empty box. */
export function toField(v: string | null | undefined): string {
  return v ?? '';
}

/**
 * The ask-first box: one question, its consequence, a danger-styled confirm and a
 * no-harm way out — in the words of the thing being acted on. Deliberately not a
 * generic `window.confirm('Are you sure?')`: a shop owner should read what actually
 * happens ("their invoices stay") and be able to say no without a stray keystroke
 * doing it anyway. DangerAction and the discard confirm both render THIS, so the two
 * asks cannot drift apart (review m4 — they started as copies in this one file).
 */
function AskFirst({
  question,
  detail,
  confirmLabel,
  onConfirm,
  pending,
  keepLabel,
  onKeep,
}: {
  question: string;
  detail: string;
  confirmLabel: string;
  onConfirm: () => void;
  pending?: boolean;
  keepLabel: string;
  onKeep: () => void;
}) {
  return (
    <div className="w-full space-y-2 rounded-[var(--radius)] border border-danger/40 bg-danger/5 p-3">
      <p className="text-sm font-medium">{question}</p>
      <p className="text-xs text-muted-foreground">{detail}</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="danger" disabled={pending} onClick={onConfirm}>
          {pending ? 'Working…' : confirmLabel}
        </Button>
        <Button variant="ghost" onClick={onKeep}>
          {keepLabel}
        </Button>
      </div>
    </div>
  );
}

/** A destructive action that asks first — a ghost button until it is asked to be sure. */
export function DangerAction({
  label,
  question,
  detail,
  onConfirm,
  pending,
}: {
  label: string;
  question: string;
  detail: string;
  onConfirm: () => void;
  pending?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <Button variant="ghost" onClick={() => setAsking(true)}>
        {label}
      </Button>
    );
  }
  return (
    <AskFirst
      question={question}
      detail={detail}
      confirmLabel={label}
      onConfirm={onConfirm}
      pending={pending}
      keepLabel="Keep it"
      onKeep={() => setAsking(false)}
    />
  );
}
